/**
 * MED-1350: check-in target weeks.
 *
 * program_weeks = max(1, ceil((end_date - start_date) / 7 days)).
 * Default midpoint week = max(1, ceil(program_weeks / 2)). Default intro week = 1.
 * Migration v258 and medley-api utils/checkinWeeks.ts use the same formula.
 */

import type { CheckinSettingsForm } from '../pages/cohorts/components/CheckinSettingsFields';

/** The check-in form of a new cohort: both check-ins off. */
export const DEFAULT_CHECKIN_SETTINGS: CheckinSettingsForm = {
  introEnabled: false,
  introWeek: null,
  midpointEnabled: false,
  midpointWeek: null,
};

export interface CheckinSettings {
  intro_checkin_enabled?: boolean;
  intro_checkin_target_week?: number | null;
  midpoint_checkin_enabled?: boolean;
  midpoint_checkin_target_week?: number | null;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The day number of a date. A Date uses its local parts (the date pickers
 * give local midnight). A string uses its YYYY-MM-DD prefix.
 */
function dayNumber(value: Date | string): number | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return (
      Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()) / DAY_MS
    );
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  return (
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])) / DAY_MS
  );
}

/** The program length in whole weeks, or null when a date is missing. */
export function programWeeks(
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined
): number | null {
  if (!startDate || !endDate) return null;
  const start = dayNumber(startDate);
  const end = dayNumber(endDate);
  if (start === null || end === null) return null;
  return Math.max(1, Math.ceil((end - start) / 7));
}

/**
 * "Week 3 · Oct 26 to Nov 1" with a start date, "Week 3" without one, and
 * undefined for no week. Shared by the create wizard and Edit Cohort Details.
 */
export function checkinWeekText(
  week: number | null | undefined,
  startDate: Date | string | null | undefined
): string | undefined {
  if (week == null || !Number.isInteger(week) || week < 1) return undefined;
  const start = startDate ? dayNumber(startDate) : null;
  if (start === null) return `Week ${week}`;
  const fmt = (day: number) =>
    new Date(day * DAY_MS).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  const from = start + (week - 1) * 7;
  return `Week ${week} · ${fmt(from)} to ${fmt(from + 6)}`;
}

/** The default midpoint check-in week, or null when the length is unknown. */
export function defaultMidpointWeek(weeks: number | null): number | null {
  return weeks === null ? null : Math.max(1, Math.ceil(weeks / 2));
}

/**
 * The error text for the first bad target week, or null when both are good.
 * A NULL or missing week is good. With no program length, any whole number of
 * at least 1 is good.
 */
export function checkinWeekError(
  input: CheckinSettings,
  weeks: number | null
): string | null {
  const fields = [
    ['intro_checkin_target_week', 'Intro'],
    ['midpoint_checkin_target_week', 'Midpoint'],
  ] as const;
  for (const [key, label] of fields) {
    const value = input[key];
    if (value === undefined || value === null) continue;
    if (!Number.isInteger(value) || value < 1) {
      return `${label} check-in target week must be a whole number of at least 1`;
    }
    if (weeks !== null && value > weeks) {
      return `${label} check-in target week must be between 1 and ${weeks}`;
    }
  }
  return null;
}

/** The program week (1 based) of a date, or null when a date is missing or before the start. */
export function weekOfDate(
  date: Date | string | null | undefined,
  startDate: Date | string | null | undefined
): number | null {
  if (!date || !startDate) return null;
  const day = dayNumber(date);
  const start = dayNumber(startDate);
  if (day === null || start === null || day < start) return null;
  return Math.floor((day - start) / 7) + 1;
}

/**
 * The error text for the check-in form of the create wizard or the edit form,
 * or null. Only a check-in that is on is checked. With a program length, a
 * check-in that is on must have a week (the form fills the default, so a
 * missing week means the CM cleared it). With no length, the week can stay
 * empty: the API stores NULL and the CM fills it in later.
 */
export function checkinsError(
  value: CheckinSettingsForm,
  weeks: number | null
): string | null {
  if (weeks !== null) {
    if (value.introEnabled && value.introWeek === null) {
      return 'Intro check-in target week must be a whole number of at least 1';
    }
    if (value.midpointEnabled && value.midpointWeek === null) {
      return 'Midpoint check-in target week must be a whole number of at least 1';
    }
  }
  return checkinWeekError(
    {
      intro_checkin_target_week: value.introEnabled ? value.introWeek : null,
      midpoint_checkin_target_week: value.midpointEnabled
        ? value.midpointWeek
        : null,
    },
    weeks
  );
}

/**
 * MED-1350: which target weeks are still "auto" in the create wizard. An auto
 * week follows the default for the current program length. A week the CM
 * typed is never changed. An empty week is always auto.
 */
export interface CheckinAutoWeeks {
  intro: boolean;
  midpoint: boolean;
}

/**
 * The auto state of a form that was not edited in this session (a new form or
 * a resumed draft). A stored week is auto only when it equals the default it
 * would get now, so a week the CM typed in an earlier session stays.
 */
export function initialCheckinAuto(
  value: CheckinSettingsForm,
  weeks: number | null
): CheckinAutoWeeks {
  return {
    intro: value.introWeek === null || value.introWeek === 1,
    midpoint:
      value.midpointWeek === null ||
      value.midpointWeek === defaultMidpointWeek(weeks),
  };
}

/**
 * Apply one change from the check-in fields. A typed week stops being auto. A
 * check-in turned on with an auto week gets the default for the current length.
 */
export function applyCheckinEdit(
  prev: CheckinSettingsForm,
  next: CheckinSettingsForm,
  auto: CheckinAutoWeeks,
  weeks: number | null
): { value: CheckinSettingsForm; auto: CheckinAutoWeeks } {
  const nextAuto = { ...auto };
  const value = { ...next };
  if (
    next.introEnabled === prev.introEnabled &&
    next.introWeek !== prev.introWeek
  ) {
    nextAuto.intro = false;
  }
  if (
    next.midpointEnabled === prev.midpointEnabled &&
    next.midpointWeek !== prev.midpointWeek
  ) {
    nextAuto.midpoint = false;
  }
  if (
    next.introEnabled &&
    !prev.introEnabled &&
    (nextAuto.intro || value.introWeek === null)
  ) {
    value.introWeek = 1;
  }
  if (
    next.midpointEnabled &&
    !prev.midpointEnabled &&
    (nextAuto.midpoint || value.midpointWeek === null)
  ) {
    value.midpointWeek = defaultMidpointWeek(weeks);
  }
  return { value, auto: nextAuto };
}

/**
 * Recompute the auto weeks of the check-ins that are on for a new program
 * length. Returns the same object when nothing changes.
 */
export function refreshAutoWeeks(
  value: CheckinSettingsForm,
  auto: CheckinAutoWeeks,
  weeks: number | null
): CheckinSettingsForm {
  const introWeek =
    value.introEnabled && (auto.intro || value.introWeek === null)
      ? 1
      : value.introWeek;
  const midpointWeek =
    value.midpointEnabled && (auto.midpoint || value.midpointWeek === null)
      ? defaultMidpointWeek(weeks)
      : value.midpointWeek;
  if (introWeek === value.introWeek && midpointWeek === value.midpointWeek) {
    return value;
  }
  return { ...value, introWeek, midpointWeek };
}

/**
 * The target week of a 1:1 on a date, capped at the program length. The last
 * day of a program that is an exact number of weeks long is in the next week
 * by weekOfDate; the cap keeps it inside the program.
 */
export function oneOnOneTargetWeek(
  date: Date | string | null | undefined,
  startDate: Date | string | null | undefined,
  weeks: number | null
): number | null {
  const week = weekOfDate(date, startDate);
  if (week === null || weeks === null) return week;
  return Math.min(week, weeks);
}
